import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useState } from 'react';
import { Panel } from '@fluentui/react';
import { useAnalytics } from '@msrceng/analytics';
import { useBoolean } from '@fluentui/react-hooks';
const defaultPanelContext = {
    closePanel: () => { },
    openPanel: () => { },
};
const PanelContext = createContext(defaultPanelContext);
/** Defines (and provides access to) the FluentUI `Panel` */
const PanelProvider = ({ children }) => {
    const { appInsights } = useAnalytics();
    const [isOpen, { setTrue: openPanel, setFalse: closePanel }] = useBoolean(false);
    const [panelDetails, setPanelDetails] = useState({
        headerText: '',
        panelContent: null,
    });
    const { headerText, panelContent } = panelDetails;
    const trackPanelEvent = (actionName, headerText) => {
        appInsights === null || appInsights === void 0 ? void 0 : appInsights.trackEvent({
            name: `${actionName} Panel`,
            properties: { headerText },
        });
    };
    const value = {
        closePanel: () => {
            closePanel();
            trackPanelEvent('Close', headerText);
        },
        isOpen,
        openPanel: (panelDetails) => {
            setPanelDetails(panelDetails);
            openPanel();
            trackPanelEvent('Open', panelDetails.headerText);
        },
    };
    return (_jsxs(PanelContext.Provider, Object.assign({ value: value }, { children: [children, _jsx(Panel, Object.assign({ closeButtonAriaLabel: 'Close', headerText: headerText, isLightDismiss: true, isOpen: isOpen, onDismiss: value.closePanel, styles: {
                    content: { paddingTop: '1rem' },
                    scrollableContent: { height: '100%' },
                } }, { children: panelContent }))] })));
};
/**
 * A hook to access the context data for the surrounding PanelProvider.
 */
const usePanel = () => {
    const context = useContext(PanelContext);
    // Since `isOpen` isn't defined in `defaultPanelContext`, it will
    // only be defined when a `PanelProvider` is correctly used.
    if (typeof context.isOpen === 'undefined') {
        throw new Error('Cannot use `usePanel` outside of a `PanelProvider`');
    }
    return context;
};
// Exports
// -------
export { PanelContext, PanelProvider, usePanel };
